import type { ActiveHeadEntry } from 'unhead/types';
import { useHeadSafe, type Meta, type UseHeadSafeInput } from '@unhead/vue';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useOSettings } from './use-settings';

type HeadMeta = Meta[];

const _pageTitle = ref('');
let pageTitleMeta: (title: string | null) => string;
const pageMeta = ref<HeadMeta>();

const DEFAULT_PAGE_TITLE = 'Onify - Helix';

/**
 * A Vue composable that provides functionalities to manage the page head.
 *
 * @function
 * @name useHPageHead
 * @description
 * This composable provides methods to update, set, and dispose of the page head,
 * including the title and meta tags.
 *
 * @returns {Object} An object containing the following methods:
 * - `hDisposeHead`: A function to dispose of the current head entry.
 * - `hUpdateHead`: A function to update the head with the current title and meta tags.
 * - `hSetTitle`: A function to set a new title and an optional title template.
 * - `hSetMeta`: A function to set new meta tags.
 * - `hPageTitle`: A computed property that returns the current page title.
 */
export function useHPageHead() {
  const head = useHeadSafe({}) as ActiveHeadEntry<UseHeadSafeInput>;
  const settings = useOSettings();

  const route = useRoute();

  function hUpdateHead() {
    head?.patch({
      title: () => pageTitleMeta?.(_pageTitle.value) ?? _pageTitle.value ?? settings?.default_page_title?.value ?? DEFAULT_PAGE_TITLE,
      meta: pageMeta.value,
    });
  }

  function hSetTitle(newTitle: string | null, titleTemplate = (title: string | null) => title ?? '') {
    _pageTitle.value = newTitle ?? '';
    pageTitleMeta = titleTemplate;
  }

  function hSetMeta(meta: HeadMeta) {
    pageMeta.value = meta;
  }

  function hDisposeHead() {
    head?.dispose();
  }

  const hPageTitle = computed(() => {
    return _pageTitle.value || route?.name || '';
  });

  const hIsTitleEmpty = computed(() => {
    return !_pageTitle.value;
  });

  return {
    hDisposeHead,
    hUpdateHead,
    hSetTitle,
    hSetMeta,
    hPageTitle,
    hIsTitleEmpty,
  };
}
